import React, { Component, createRef } from 'react';
import ReactDOM from 'react-dom';

import CustomButton from './CustomButton';
import CustomButtonGroup from './CustomButtonGroup';
import CustomModalFooter from './CustomModalFooter';
import CustomModalTransition from './CustomModalTransition';

// ─── Width map matching Atlaskit's modal-dialog sizes ───────────────────────
const WIDTH_MAP = {
    small: 'custom-modal--small',
    medium: 'custom-modal--medium',
    large: 'custom-modal--large',
    'x-large': 'custom-modal--x-large'
};

// ─── CustomModal ─────────────────────────────────────────────────────────────

/**
 * Full-featured custom modal that replaces @atlaskit/modal-dialog's <Modal>.
 *
 * Props (matching Atlaskit's API):
 *   - autoFocus {boolean}
 *   - components.Header {Function|Component}
 *   - footer {Function} — render prop receiving { showKeyline }
 *   - height {string}
 *   - i18n {Object}
 *   - onClose {Function}
 *   - onDialogDismissed {Function}
 *   - shouldCloseOnEscapePress {boolean}
 *   - shouldCloseOnOverlayClick {boolean}
 *   - width {string|number} — 'small'|'medium'|'large'|'x-large' or px/% string
 *   - className {string}
 *   - children
 */
class CustomModal extends Component {
    /**
     * Initializes a new CustomModal instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props) {
        super(props);
        this._boxRef = createRef();
        this._prevFocus = null;
        this.state = { phase: 'entering' }; // 'entering' | 'entered' | 'exiting'
        this._onKeyDown = this._onKeyDown.bind(this);
        this._onBlanketClick = this._onBlanketClick.bind(this);
        this._onBoxClick = this._onBoxClick.bind(this);
    }

    // ── Lifecycle ────────────────────────────────────────────────────────────

    /**
     * Set up event listeners and enter timers when component mounts.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        // Save previously focused element so we can restore it on close
        this._prevFocus = document.activeElement;

        // Focus the dialog box
        if (this.props.autoFocus && this._boxRef.current) {
            this._boxRef.current.focus();
        }

        // Global key listener
        document.addEventListener('keydown', this._onKeyDown);

        // Transition to 'entered' after animation completes
        this._enterTimer = setTimeout(() => {
            this.setState({ phase: 'entered' });
        }, 220);
    }

    /**
     * Clean up event listeners and timers when component unmounts.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        document.removeEventListener('keydown', this._onKeyDown);
        clearTimeout(this._enterTimer);

        // Restore focus to the element that was focused before the dialog opened
        if (this._prevFocus && this._prevFocus.focus) {
            try {
                this._prevFocus.focus();
            } catch (_) { /* ignore */ }
        }
    }

    // ── Event Handlers ───────────────────────────────────────────────────────

    /**
     * Keydown handler to dismiss the modal when Escape is pressed.
     *
     * @param {KeyboardEvent} e - The keydown event.
     * @returns {void}
     */
    _onKeyDown(e) {
        if (e.key === 'Escape' && this.props.shouldCloseOnEscapePress !== false) {
            e.stopPropagation();
            this._dismiss();
        }
    }

    /**
     * Click handler for the blanket overlay to dismiss the modal.
     *
     * @param {MouseEvent} e - The click event.
     * @returns {void}
     */
    _onBlanketClick(e) {
        if (this.props.shouldCloseOnOverlayClick !== false) {
            e.stopPropagation();
            this._dismiss();
        }
    }

    /**
     * Click handler for the dialog box to stop event propagation.
     *
     * @param {MouseEvent} e - The click event.
     * @returns {void}
     */
    _onBoxClick(e) {
        // Stop blanket handler from triggering when clicking inside the dialog
        e.stopPropagation();
    }

    /**
     * Triggers onClose and onDialogDismissed callback props.
     *
     * @returns {void}
     */
    _dismiss() {
        const { onClose, onDialogDismissed } = this.props;

        onDialogDismissed && onDialogDismissed();
        onClose && onClose();
    }

    // ── Render ───────────────────────────────────────────────────────────────

    /**
     * Resolves the CSS width class based on the width prop.
     *
     * @returns {string}
     */
    _resolveWidthClass() {
        const { width = 'medium' } = this.props;

        return WIDTH_MAP[width] || 'custom-modal--medium';
    }

    /**
     * Renders the header slot if a header component is provided.
     *
     * @returns {ReactElement|null}
     */
    _renderHeader() {
        const { components = {} } = this.props;
        const HeaderComponent = components.Header;

        if (!HeaderComponent) {
            return null;
        }

        // Atlaskit passes showKeyline + id to the header
        return (
            <HeaderComponent
                id = 'custom-modal-title'
                showKeyline = { false } />
        );
    }

    /**
     * Renders the footer slot using the footer render prop.
     *
     * @returns {ReactElement|null}
     */
    _renderFooter() {
        const { footer, disableFooter } = this.props;

        if (disableFooter || !footer) {
            return null;
        }

        // footer is a render prop: footer({ showKeyline })
        return footer({ showKeyline: true });
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { phase } = this.state;
        const { className = '' } = this.props;
        const widthClass = this._resolveWidthClass();
        const phaseClass = phase === 'entered' ? '' : `custom-modal-portal--${phase}`;

        const content = (
            <div className = { `custom-dialog-portal ${phaseClass}`.trim() }>

                {/* Blanket */}
                <div
                    className = 'custom-modal-blanket'
                    onClick = { this._onBlanketClick } />

                {/* Positioner */}
                <div
                    className = 'custom-modal-positioner'
                    onClick = { this._onBlanketClick }>

                    {/* Dialog box */}
                    <div
                        aria-labelledby = 'custom-modal-title'
                        aria-modal = { true }
                        className = { `custom-modal-box ${widthClass} ${className}`.trim() }
                        onClick = { this._onBoxClick }
                        ref = { this._boxRef }
                        role = 'dialog'
                        tabIndex = { -1 }>

                        {/* Header slot */}
                        {this._renderHeader()}

                        {/* Body */}
                        <div className = 'custom-modal-body'>
                            {this.props.children}
                        </div>

                        {/* Footer slot */}
                        {this._renderFooter()}
                    </div>
                </div>
            </div>
        );

        // Portal into document.body (same as atlaskit-portal)
        return ReactDOM.createPortal(content, document.body);
    }
}

export { CustomButton, CustomButtonGroup, CustomModalFooter, CustomModalTransition, CustomModal };
export default CustomModal;
